import { useState, useEffect } from "react";
import { ModalProps } from "shared/types/modal";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./style.module.scss";
import CustomModal from "shared/components/common/customModal";
import {
  AddProjectDesignerService,
  ProjectDesignerRole,
} from "shared/services/project";
import { handleErrors } from "shared/utils/helper";
import { toastMessage } from "shared/components/common/toast";

export type JoinRole = "designer" | "finalReviewer" | "both";

const JOIN_ROLE_TO_PROJECT_DESIGNER: Record<JoinRole, ProjectDesignerRole> = {
  designer: ProjectDesignerRole.DESIGNER,
  finalReviewer: ProjectDesignerRole.FINAL_REVIEWER,
  both: ProjectDesignerRole.BOTH,
};

const getInitials = (name: string): string => {
  if (!name) return "??";
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words.length === 1 && words[0].length >= 2)
    return words[0].substring(0, 2).toUpperCase();
  if (words.length === 1 && words[0].length === 1)
    return (words[0][0] + words[0][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase() || "??";
};

const hasValidImage = (image: string | null | undefined): boolean => {
  if (image === null || image === undefined) return false;
  if (typeof image !== "string") return false;
  const trimmed = String(image).trim();
  return (
    trimmed !== "" &&
    trimmed !== "null" &&
    trimmed !== "undefined" &&
    trimmed.length > 0
  );
};

interface Props extends ModalProps {
  projectId: number;
  projectTitle?: string;
  projectImage?: string | null;
  onSuccess?: (role: JoinRole) => void;
}

const ROLE_OPTIONS: {
  value: JoinRole;
  title: string;
  description: string;
}[] = [
  {
    value: "designer",
    title: "Join as Designer only",
    description:
      "Upload revisions, Work on change requests, Communicate with client",
  },
  {
    value: "finalReviewer",
    title: "Join as Final Reviewer only",
    description: "Review final designs, Mark project completed",
  },
  {
    value: "both",
    title: "Join as Both",
    description: "Design, Review final stage, Complete project",
  },
];

const JoinProjectModal = ({
  projectId,
  projectTitle = "Project",
  projectImage,
  show,
  handleClose,
  onSuccess,
}: Props) => {
  const [selectedRole, setSelectedRole] = useState<JoinRole>("designer");
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [projectImage, show]);

  if (!show) return null;

  const handleJoin = () => {
    if (!projectId) return;
    setLoading(true);
    AddProjectDesignerService(projectId, JOIN_ROLE_TO_PROJECT_DESIGNER[selectedRole])
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          handleClose();
          toastMessage("success", "Successfully joined the project");
          onSuccess?.(selectedRole);
        } else {
          handleErrors(res);
        }
      })
      .catch((err) => {
        handleErrors(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <CustomModal
      isOpen={show}
      size="md"
      onClose={handleClose}
      title=""
      description=""
      showBackButton={false}
      customContentContainer="noSidePadding"
      cancelButtonText=""
      cancelButtonContainerStyle={styles.customCancelButton}
      cancelButtonTitleStyle={styles.customCancelButtonTitle}
      actionButtonContainerStyle={styles.customActionButton}
      actionButtonTitleStyle={styles.customActionTitle}
      actionButtonText="Join Project"
      actionButtonIcon={Icons.FileAcceptIcon2}
      showModalHeader
      showModalFooter
      showCancelButton={false}
      onNext={handleJoin}
      actionButtonLoading={loading}
      actionButtonDisabled={loading}
      showHeaderBorder={false}
      showFooterBorder={false}
    >
      <div className={classNames(styles.contentContainer)}>
        <div
          className={classNames(
            styles.modalBody,
            "w-full flex items-center justify-center flex-col gap-4",
          )}
        >
          <div className={styles.projectImageContainer}>
            {hasValidImage(projectImage) && !imageError ? (
              <img
                src={projectImage!}
                alt={projectTitle}
                className={styles.projectImage}
                onError={() => setImageError(true)}
              />
            ) : (
              <div className={styles.projectImageFallback}>
                {projectTitle ? (
                  <span className={styles.projectImageInitials}>
                    {getInitials(projectTitle)}
                  </span>
                ) : (
                  <Icons.ProjectsIcon className={styles.projectIcon} />
                )}
              </div>
            )}
          </div>
          <h2 className={styles.modalTitle}>Join {projectTitle}</h2>

          <div className={styles.roleOptions}>
            {ROLE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={classNames(
                  styles.roleOption,
                  selectedRole === option.value && styles.roleOptionSelected,
                )}
                onClick={() => setSelectedRole(option.value)}
              >
                <span className={styles.radioWrapper}>
                  {selectedRole === option.value ? (
                    <Icons.TickCircleIcon className={styles.radioSelected} />
                  ) : (
                    <span className={styles.radioEmpty} />
                  )}
                </span>
                <div className={styles.roleContent}>
                  <span className={styles.roleTitle}>{option.title}</span>
                  <span className={styles.roleDescription}>
                    {option.description}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* <CustomButton
            IconDirection="left"
            Icon={() => (
              <span className={styles.plusCircleIcon}>
                <Icons.Plus />
              </span>
            )}
            title="Join Project"
            onClick={handleJoin}
            loading={loading}
            disabled={loading}
            containerStyle={classNames(styles.joinButton, "primary-btn")}
            titleStyle={styles.joinButtonText}
          /> */}
        </div>
      </div>
    </CustomModal>
  );
};

export default JoinProjectModal;
